/**
 * Note feedback styles - layout plus the filled-thumb tint. Colors come from
 * the theme palette (success for thumbs up, error for thumbs down).
 */

import { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

type Tone = 'success' | 'error';

const filledTint = (tone: Tone) => ({
  color: `${tone}.main`,
  bgcolor: (theme: Theme) => alpha(theme.palette[tone].main, 0.12),
});

const filledThumb = (tone: Tone) => ({
  ...filledTint(tone),
  '&:hover': {
    bgcolor: (theme: Theme) => alpha(theme.palette[tone].main, 0.2),
  },
});

const thumbBadge = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export const noteFeedbackStyles = {
  // Keeps the feedback row flush with the export footer below it.
  footerStack: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  } as SxProps<Theme>,
  container: {
    borderTop: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
    px: 2,
    py: 1,
    flexShrink: 0,
  } as SxProps<Theme>,
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    minHeight: 32,
  } as SxProps<Theme>,
  prompt: {
    color: 'text.secondary',
    mr: 0.5,
  } as SxProps<Theme>,
  outlineThumb: {
    color: 'text.secondary',
  } as SxProps<Theme>,
  filledThumbUp: filledThumb('success') as SxProps<Theme>,
  filledThumbDown: filledThumb('error') as SxProps<Theme>,
  thumbBadgeUp: { ...thumbBadge, ...filledTint('success') } as SxProps<Theme>,
  thumbBadgeDown: { ...thumbBadge, ...filledTint('error') } as SxProps<Theme>,
  confirmation: {
    color: 'text.secondary',
  } as SxProps<Theme>,
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    outline: 'none',
  } as SxProps<Theme>,
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  } as SxProps<Theme>,
  panelTitle: {
    flex: 1,
    fontWeight: 600,
    color: 'text.primary',
  } as SxProps<Theme>,
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
  } as SxProps<Theme>,
  characterCount: {
    alignSelf: 'flex-end',
    mt: -0.5,
  } as SxProps<Theme>,
  failureRow: {
    outline: 'none',
  } as SxProps<Theme>,
  failureText: {
    color: 'error.main',
  } as SxProps<Theme>,
  // Retry reads as a word in the failure sentence, at the sentence's size.
  retryButton: {
    fontSize: 'inherit',
    lineHeight: 'inherit',
    verticalAlign: 'baseline',
    minWidth: 0,
    p: 0,
  } as SxProps<Theme>,
  panelFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as SxProps<Theme>,
  submitButton: {
    width: 'auto',
    px: 3,
  } as SxProps<Theme>,
  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    p: 0,
    m: '-1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  } as SxProps<Theme>,
};
